import { useEffect, useRef, useState } from 'react';
import { X, Plus, Trash2, Save, ChevronDown } from 'react-feather';
import {
  getFeatureConfigsHandler,
  createFeatureConfigHandler,
  updateFeatureConfigHandler,
  patchFeatureConfigHandler,
} from '../../services/documentForgery';
import { showSuccessToast, showErrorToast } from '../../utility/helper';

const FIELD_TYPES = [
  { value: 'string',  label: 'Text' },
  { value: 'number',  label: 'Number' },
  { value: 'date',    label: 'Date' },
  { value: 'boolean', label: 'Yes / No' },
];

const EMPTY_FIELD = { key: '', label: '', type: 'string', description: '' };

// ── Small field row ────────────────────────────────────────────────────────────
const FieldRow = ({ field, idx, onChange, onRemove }) => (
  <div style={{
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 110px 32px',
    gap: 8, alignItems: 'center',
    padding: '8px 0',
    borderBottom: '1px solid #f3f4f6',
  }}>
    <input
      placeholder="Field key (e.g. po_number)"
      value={field.key}
      onChange={e => onChange(idx, 'key', e.target.value.replace(/\s+/g, '_').toLowerCase())}
      style={{
        padding: '7px 10px', borderRadius: 7, border: '1px solid #d1d5db',
        fontSize: 12, outline: 'none', color: '#111827',
      }}
    />
    <input
      placeholder="Display label"
      value={field.label}
      onChange={e => onChange(idx, 'label', e.target.value)}
      style={{
        padding: '7px 10px', borderRadius: 7, border: '1px solid #d1d5db',
        fontSize: 12, outline: 'none', color: '#111827',
      }}
    />
    <select
      value={field.type}
      onChange={e => onChange(idx, 'type', e.target.value)}
      style={{
        padding: '7px 8px', borderRadius: 7, border: '1px solid #d1d5db',
        fontSize: 12, color: '#111827', background: '#fff', cursor: 'pointer',
      }}
    >
      {FIELD_TYPES.map(t => (
        <option key={t.value} value={t.value}>{t.label}</option>
      ))}
    </select>
    <button
      onClick={() => onRemove(idx)}
      style={{
        width: 28, height: 28, borderRadius: 6, border: 'none',
        background: '#fee2e2', color: '#dc2626',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer', flexShrink: 0,
      }}
    >
      <Trash2 size={13} />
    </button>
  </div>
);

// ── Main component ─────────────────────────────────────────────────────────────
const CustomFieldsPopup = ({ onClose }) => {
  const [docType,   setDocType]   = useState('INVOICE');
  const [fields,    setFields]    = useState([]);
  const [isActive,  setIsActive]  = useState(true);
  const [configId,  setConfigId]  = useState(null);   // existing config id if any
  const [loading,   setLoading]   = useState(true);
  const [saving,    setSaving]    = useState(false);
  const backdropRef = useRef(null);

  // Load existing config when docType changes
  useEffect(() => {
    setLoading(true);
    getFeatureConfigsHandler()
      .then(res => {
        const all    = res?.data?.results || res?.data || [];
        const config = Array.isArray(all)
          ? all.find(c => c.doc_type === docType)
          : null;
        if (config) {
          setFields(config.fields || []);
          setIsActive(config.is_active !== false);
          setConfigId(config.id);
        } else {
          setFields([]);
          setIsActive(true);
          setConfigId(null);
        }
      })
      .catch(() => showErrorToast('Failed to load custom fields'))
      .finally(() => setLoading(false));
  }, [docType]);

  const handleFieldChange = (idx, key, val) => {
    setFields(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [key]: val };
      return next;
    });
  };

  const handleAddField = () => {
    setFields(prev => [...prev, { ...EMPTY_FIELD }]);
  };

  const handleRemoveField = (idx) => {
    setFields(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    // Validate — all fields need key and label
    const invalid = fields.filter(f => !f.key.trim() || !f.label.trim());
    if (invalid.length > 0) {
      showErrorToast('All fields need a key and a label.');
      return;
    }

    setSaving(true);
    try {
      const payload = { doc_type: docType, fields, is_active: isActive };

      if (configId) {
        await updateFeatureConfigHandler(configId, payload);
      } else {
        await createFeatureConfigHandler(payload);
      }
      showSuccessToast('Custom fields saved successfully');
      onClose?.();
    } catch (err) {
      showErrorToast(
        err?.response?.data?.detail || 'Failed to save custom fields'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      ref={backdropRef}
      onClick={e => { if (e.target === backdropRef.current) onClose?.(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 300,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
    >
      <div style={{
        background: '#fff', borderRadius: 14,
        width: '100%', maxWidth: 600,
        maxHeight: '88vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.22)',
        overflow: 'hidden',
      }}>
        {/* ── Header ── */}
        <div style={{
          padding: '18px 20px 14px',
          borderBottom: '1px solid #f3f4f6',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#111827' }}>Custom Field Configuration</div>
            <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>
              Define extra fields the LLM should extract from each document
            </div>
          </div>
          <button onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 4, display: 'flex' }}>
            <X size={18} />
          </button>
        </div>

        {/* ── Doc type selector + active toggle ── */}
        <div style={{
          padding: '12px 20px', background: '#f9fafb',
          borderBottom: '1px solid #f3f4f6',
          display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>Document Type:</span>
            <div style={{ position: 'relative' }}>
              <select
                value={docType}
                onChange={e => setDocType(e.target.value)}
                style={{
                  padding: '6px 32px 6px 10px', borderRadius: 7,
                  border: '1px solid #d1d5db', fontSize: 13,
                  color: '#111827', background: '#fff', cursor: 'pointer',
                  appearance: 'none',
                }}
              >
                <option value="INVOICE">Invoice</option>
                <option value="BILL">Bill</option>
              </select>
              <ChevronDown size={13} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#6b7280' }} />
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>Active</span>
            <div
              onClick={() => setIsActive(v => !v)}
              style={{
                width: 38, height: 22, borderRadius: 11,
                background: isActive ? '#44ace4' : '#d1d5db',
                position: 'relative', cursor: 'pointer', transition: 'background 0.2s',
              }}
            >
              <div style={{
                position: 'absolute', top: 3,
                left: isActive ? 19 : 3,
                width: 16, height: 16, borderRadius: '50%',
                background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                transition: 'left 0.2s',
              }} />
            </div>
          </div>
        </div>

        {/* ── Field list ── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 20px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', color: '#9ca3af', padding: '32px 0', fontSize: 13 }}>
              Loading…
            </div>
          ) : (<>
            {/* Column headers */}
            {fields.length > 0 && (
              <div style={{
                display: 'grid', gridTemplateColumns: '1fr 1fr 110px 32px',
                gap: 8, padding: '10px 0 4px',
                fontSize: 10, fontWeight: 700, color: '#9ca3af',
                textTransform: 'uppercase', letterSpacing: '0.4px',
                position: 'sticky', top: 0, background: '#fff', zIndex: 2,
              }}>
                <span>Field Key</span>
                <span>Display Label</span>
                <span>Type</span>
                <span />
              </div>
            )}

            {fields.map((field, idx) => (
              <FieldRow
                key={idx}
                field={field}
                idx={idx}
                onChange={handleFieldChange}
                onRemove={handleRemoveField}
              />
            ))}

            {fields.length === 0 && (
              <div style={{ textAlign: 'center', color: '#9ca3af', padding: '32px 0' }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>📋</div>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>No custom fields yet</div>
                <div style={{ fontSize: 12 }}>Click "Add Field" to define fields the AI should extract</div>
              </div>
            )}

            {/* Add field button */}
            <button
              onClick={handleAddField}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                margin: '12px 0', padding: '8px 14px', borderRadius: 8,
                border: '1px dashed #44ace4', background: '#f0f9ff',
                color: '#44ace4', fontSize: 12, fontWeight: 700, cursor: 'pointer',
              }}
            >
              <Plus size={13} /> Add Field
            </button>
          </>)}
        </div>

        {/* ── Footer ── */}
        <div style={{
          padding: '12px 20px', borderTop: '1px solid #f3f4f6',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div style={{ fontSize: 11, color: '#9ca3af' }}>
            {fields.length} field{fields.length !== 1 ? 's' : ''} configured
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={onClose}
              style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#f9fafb', fontSize: 13, fontWeight: 600, color: '#6b7280', cursor: 'pointer' }}>
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 20px', borderRadius: 8, border: 'none',
                background: '#44ace4', color: '#fff',
                fontSize: 13, fontWeight: 700, cursor: saving ? 'wait' : 'pointer',
              }}>
              <Save size={13} />
              {saving ? 'Saving…' : 'Save Fields'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomFieldsPopup;